/**
 * GRI v2 Workspace - Main Dashboard
 * At-a-glance GRI readiness and navigation hub
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, Target, AlertTriangle, CheckCircle, FileText,
  TrendingUp, Clock, Users, ChevronRight, Download, RefreshCw,
  Clipboard, BookOpen, Leaf, Shield
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIv2Workspace = ({ study, onNavigate }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [uploadingCSV, setUploadingCSV] = useState(false);

  useEffect(() => {
    if (study?.id) {
      fetchDashboard();
    }
  }, [study?.id]);

  const fetchDashboard = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/workspace-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching GRI v2 dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoPopulate = async () => {
    setAutoPopulating(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/gri-v2/${study.id}/auto-populate-disclosures`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess(`Success! ${res.data.populated_count} disclosures auto-populated from SAARV modules.`);
      fetchDashboard(); // Refresh data
    } catch (error) {
      showError('Error auto-populating disclosures: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAutoPopulating(false);
    }
  };

  const handleDownloadTemplate = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/download-csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GRI_Disclosures_Template_${study.id.substring(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Error downloading template: ' + error.message);
    }
  };

  const handleUploadCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingCSV(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/api/gri-v2/${study.id}/upload-csv-data`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      showSuccess(`Success! ${res.data.uploaded_count} disclosures uploaded.`);
      fetchDashboard(); // Refresh data
    } catch (error) {
      showError('Error uploading CSV: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingCSV(false);
      event.target.value = ''; // Reset file input
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading GRI workspace...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return <div className="text-center text-gray-600 py-12">No dashboard data available</div>;
  }

  const coveragePercentage = dashboard.coverage?.percentage || 0;
  const coverageColor = coveragePercentage >= 80 ? 'green' : coveragePercentage >= 50 ? 'yellow' : 'red';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">GRI Reporting Workspace</h2>
            <p className="text-indigo-100 mb-1">{dashboard.study_name}</p>
            <p className="text-sm text-indigo-200">Reporting Period: {dashboard.reporting_period}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAutoPopulate}
              disabled={autoPopulating}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {autoPopulating ? 'Populating...' : 'Auto-Populate'}
            </button>
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition"
            >
              <Download className="w-4 h-4" />
              CSV Template
            </button>
            <label className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition cursor-pointer">
              <FileText className="w-4 h-4" />
              {uploadingCSV ? 'Uploading...' : 'Upload CSV'}
              <input
                type="file"
                accept=".csv"
                onChange={handleUploadCSV}
                className="hidden"
                disabled={uploadingCSV}
              />
            </label>
            <button
              onClick={fetchDashboard}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2 transition"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* GRI Coverage Meter */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              GRI Coverage Meter
            </h3>
            <span className={`text-2xl font-bold ${
              coverageColor === 'green' ? 'text-green-600' :
              coverageColor === 'yellow' ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {coveragePercentage}%
            </span>
          </div>

          {/* Progress Bar */}
          <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden mb-4">
            <div 
              className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                coverageColor === 'green' ? 'bg-green-500' :
                coverageColor === 'yellow' ? 'bg-yellow-500' :
                'bg-red-500'
              }`}
              style={{ width: `${coveragePercentage}%` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-gray-700">
              {dashboard.coverage.complete} / {dashboard.coverage.total} Disclosures Complete
            </div>
          </div>

          {/* Coverage Breakdown */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-700">{dashboard.coverage.complete}</div>
              <div className="text-xs text-green-600">Complete</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-700">{dashboard.coverage.partial}</div>
              <div className="text-xs text-yellow-600">Partial</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-2xl font-bold text-red-700">{dashboard.coverage.missing}</div>
              <div className="text-xs text-red-600">Missing</div>
            </div>
          </div>
        </div>

        {/* Data Health */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Data Health
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-700">Green (Ready)</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{dashboard.data_health.green}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm text-gray-700">Amber (Partial)</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{dashboard.data_health.amber}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-sm text-gray-700">Red (Missing)</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{dashboard.data_health.red}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Material Topics Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Clipboard className="w-5 h-5 text-purple-600" />
              Material Topics
            </h3>
            {dashboard.material_topics.completed && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-purple-700">{dashboard.material_topics.count}</div>
              <div className="text-sm text-gray-600">Topics Identified</div>
            </div>
            <button
              onClick={() => onNavigate('materiality')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
            >
              Review Topics
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Board Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Board Actions
            </h3>
            {dashboard.board_actions.pending_approvals === 0 && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-orange-700">{dashboard.board_actions.pending_approvals}</div>
              <div className="text-sm text-gray-600">Pending Approvals</div>
            </div>
            <Clock className="w-8 h-8 text-orange-300" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Navigation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => onNavigate('materiality')}
            className="flex flex-col items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-400 transition group"
          >
            <Target className="w-8 h-8 text-purple-600 group-hover:scale-110 transition" />
            <div className="text-center">
              <div className="font-semibold text-gray-900">Material Topics</div>
              <div className="text-xs text-gray-600">GRI 3 Assessment</div>
            </div>
          </button>

          <button
            onClick={() => onNavigate('disclosure-index')}
            className="flex flex-col items-center gap-3 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg border-2 border-indigo-200 hover:border-indigo-400 transition group"
          >
            <FileText className="w-8 h-8 text-indigo-600 group-hover:scale-110 transition" />
            <div className="text-center">
              <div className="font-semibold text-gray-900">Disclosure Index</div>
              <div className="text-xs text-gray-600">Master Table</div>
            </div>
          </button>

          <button
            onClick={() => onNavigate('general-disclosures')}
            className="flex flex-col items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-400 transition group"
          >
            <Shield className="w-8 h-8 text-blue-600 group-hover:scale-110 transition" />
            <div className="text-center">
              <div className="font-semibold text-gray-900">GRI 2</div>
              <div className="text-xs text-gray-600">General Disclosures</div>
            </div>
          </button>

          <button
            onClick={() => onNavigate('standards-navigator')}
            className="flex flex-col items-center gap-3 p-4 bg-teal-50 hover:bg-teal-100 rounded-lg border-2 border-teal-200 hover:border-teal-400 transition group"
          >
            <BookOpen className="w-8 h-8 text-teal-600 group-hover:scale-110 transition" />
            <div className="text-center">
              <div className="font-semibold text-gray-900">Standards Navigator</div>
              <div className="text-xs text-gray-600">Full GRI Structure</div>
            </div>
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">GRI Readiness Score: {coveragePercentage}%</p>
            <p>
              {coveragePercentage >= 80 
                ? 'Excellent! Your GRI reporting is nearly complete and audit-ready.' 
                : coveragePercentage >= 50 
                ? 'Good progress. Focus on completing partial disclosures to improve audit readiness.' 
                : 'Getting started. Use the Quick Navigation above to complete key sections.'}
            </p>
            {dashboard.last_updated && (
              <p className="text-xs text-blue-700 mt-2">Last updated: {new Date(dashboard.last_updated).toLocaleString()}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GRIv2Workspace;
